import React, { useState, useEffect } from 'react';
import { Mail, Phone, Bell, Shield, CheckCircle, X } from 'lucide-react';
import { getPageBySlug } from '../lib/pageService';

interface NewsletterContent {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  successTitle?: string;
  successMessage?: string;
  emailPlaceholder?: string;
  phonePlaceholder?: string;
  kvkkText?: string;
  marketingText?: string;
}

const Newsletter = () => {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [content, setContent] = useState<NewsletterContent>({
    title: "Fırsatlardan Haberdar Olun",
    subtitle: "En güncel banka kampanyaları, uygun kredi fırsatları ve size özel tekliflerden ilk siz haberdar olun.",
    buttonText: "Fırsatlardan Haberdar Ol",
    successTitle: "Teşekkürler!",
    successMessage: "Başvurunuz başarıyla alındı. En güncel kampanya ve fırsatlardan haberdar olmak için e-posta ve SMS bildirimlerinizi takip edebilirsiniz.",
    emailPlaceholder: "E-posta Adresi",
    phonePlaceholder: "Telefon Numarası",
    kvkkText: "Kişisel verilerimin işlenmesine ilişkin Aydınlatma Metni'ni okudum ve kişisel verilerimin bu kapsamda işlenmesini kabul ediyorum.",
    marketingText: "KrediPark'a özel kampanyalar, avantajlı teklifler ve güncel fırsatlardan e-posta ve SMS yoluyla haberdar olmak istiyorum."
  });

  useEffect(() => {
    const fetchNewsletterContent = async () => {
      try {
        const page = await getPageBySlug('anasayfa');
        if (page && page.content) {
          try {
            let pageContent;
            if (typeof page.content === 'string') {
              pageContent = JSON.parse(page.content);
            } else {
              pageContent = page.content;
            }
            
            if (pageContent.newsletter) {
              setContent({
                ...content,
                ...pageContent.newsletter
              });
            }
          } catch (error) {
            console.error('Error parsing page content:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching newsletter content:', error);
      }
    };

    fetchNewsletterContent();
  }, []);

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (phone && email && consent1) {
      setShowSuccess(true);
      // Reset form
      setPhone('');
      setEmail('');
      setConsent1(false);
      setConsent2(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-slate-200">
              <div className="max-w-lg mx-auto text-center">
                <div className="w-16 h-16 bg-[#F7BA45]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#F7BA45]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  {content.successTitle}
                </h2>
                <p className="text-slate-600 mb-8">
                  {content.successMessage}
                </p>
                <button
                  onClick={() => setShowSuccess(false)}
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 font-medium rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-slate-200">
            <div className="flex flex-col md:flex-row gap-12">
              {/* Left Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[#F7BA45] mb-4">
                  <Bell className="h-6 w-6" />
                  <h2 className="text-2xl font-bold text-slate-800">
                    {content.title}
                  </h2>
                </div>
                
                <p className="text-slate-600 mb-8">
                  {content.subtitle}
                </p>

                <div className="space-y-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={content.phonePlaceholder}
                      className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-lg 
                        placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#F7BA45]/20 
                        focus:border-[#F7BA45] transition-colors"
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={content.emailPlaceholder}
                      className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-lg 
                        placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#F7BA45]/20 
                        focus:border-[#F7BA45] transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Right Content */}
              <div className="flex-1">
                <div className="space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex items-start">
                      <div className="bg-white border rounded w-6 h-6 flex flex-shrink-0 justify-center items-center mr-2 
                        focus-within:border-[#F7BA45] transition-colors">
                        <input
                          type="checkbox"
                          className="opacity-0 absolute"
                          checked={consent1}
                          onChange={(e) => setConsent1(e.target.checked)}
                        />
                        <CheckCircle 
                          className={`${consent1 ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                        />
                      </div>
                    </div>
                    <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                      {content.kvkkText}
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex items-start">
                      <div className="bg-white border rounded w-6 h-6 flex flex-shrink-0 justify-center items-center mr-2 
                        focus-within:border-[#F7BA45] transition-colors">
                        <input
                          type="checkbox"
                          className="opacity-0 absolute"
                          checked={consent2}
                          onChange={(e) => setConsent2(e.target.checked)}
                        />
                        <CheckCircle 
                          className={`${consent2 ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                        />
                      </div>
                    </div>
                    <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                      {content.marketingText}
                    </span>
                  </label>
                </div>

                <div className="mt-8">
                  <button 
                    onClick={handleSubmit}
                    className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 font-medium px-8 py-3 rounded-lg 
                      transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                  >
                    <Bell size={20} />
                    <span>{content.buttonText}</span>
                  </button>
                  <p className="text-xs text-slate-500 text-center mt-3">
                    <Shield className="inline-block h-4 w-4 mr-1" />
                    Bilgileriniz SSL sertifikası ile güvende
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Newsletter;